import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";

const URL = "https://palstars.com/cards/frostallion-ssp-market-value";
const TITLE = "Frostallion TCG Card Value & Set Status | Pal Stars";
const DESCRIPTION =
  "Frostallion Palworld TCG card status: what the official card list publishes today, why no price exists yet, and where its market board will appear.";

const FAQ: FaqItem[] = [
  {
    q: "Is there an official Frostallion Palworld TCG card?",
    a: "Not yet. The official English and Japanese card lists return no Frostallion entry, so there is no printed code, rarity, ability text or scan — and therefore no market value to report.",
  },
  {
    q: "Is Frostallion Legend a real card?",
    a: "No. \"Legend\" is not a printed Palworld TCG rarity; it describes legendary Pals in the video game. Any listing advertising a Frostallion Legend card is not describing an officially published printing.",
  },
  {
    q: "What would a Frostallion card do in a deck?",
    a: "Nothing can be stated about its abilities until the official effect text is published. Ice-aligned control lines in the current sets are the closest read on how such a card would slot in, and those are covered on our deck pages.",
  },
  {
    q: "How will I know when the card is added?",
    a: "The card database syncs against the official card list, so a new Frostallion printing appears on this page and on the price chart automatically, with its own asking board and TCGplayer sold history.",
  },
];

export const Route = createFileRoute("/cards/frostallion-ssp-market-value")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld TCG Cards", "/cards"],
        ["Frostallion card value", "/cards/frostallion-ssp-market-value"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: FrostallionValuePage,
});

function FrostallionValuePage() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · card status tracker</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Official Frostallion TCG Variant Price History &amp; Set Checklist
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          There is no published Frostallion Palworld TCG card. Both the English and Japanese official card lists return
          no Frostallion entry, which means no printed code, no rarity, no ability text, no scan and no price history.
          This page holds that status and fills with a live board the moment an official entry appears.
        </p>
      </header>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Ledger status and what we verify</h2>
        <p>
          We only add a card after the official list publishes it. That rule keeps invented codes and speculative prices
          out of the price chart, the index and the collection tracker — a fabricated entry would distort every board it
          touches. Frostallion stays listed as unconfirmed until that changes.
        </p>
        <ul className="space-y-2">
          <li>
            <strong className="text-foreground">English card list:</strong> no Frostallion result.
          </li>
          <li>
            <strong className="text-foreground">Japanese card list:</strong> no Frostallion result.
          </li>
          <li>
            <strong className="text-foreground">Rarity note:</strong> "Legend" is not a printed Palworld TCG rarity.
          </li>
        </ul>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Track the market it would join</h2>
        <p>
          Ice-aligned control cards from the released sets already have full price coverage on the{" "}
          <Link to="/prices" className="text-primary hover:underline">
            Palworld TCG Price Chart
          </Link>
          , which is the fastest way to see how a new defensive chase printing would be valued.
        </p>
        <ul className="space-y-2">
          <li>
            <Link to="/tcg-price-index" className="text-primary hover:underline">
              Palworld TCG price index market trends
            </Link>
          </li>
          <li>
            <Link to="/cards/jetragon-ssp-market-value" className="text-primary hover:underline">
              Jetragon card status tracker
            </Link>
          </li>
          <li>
            <Link to="/cards" className="text-primary hover:underline">
              Full Palworld TCG card list
            </Link>
          </li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Frostallion TCG card FAQ" />
    </div>
  );
}
